// react
import { useContext } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject } from '../../pages/Project/Project.context';

// ui
import { CardKeyStats } from '../CardKeyStats/CardKeyStats';
import { CardSeverity, CardSeverityProps } from '../CardSeverity/CardSeverity';

// dialogs
import { DialogUtilizationProps } from '../../dialogs/DialogUtilization/DialogUtilization';

// utils
import { formatCurrency } from '../../../utils/currency';
import { formatPercentage } from '../../../utils/format';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface CardUtilizationProps {
	card?: 'KEY_STATS' | 'SEVERITY';
	DialogProps?: Partial<DialogUtilizationProps>;
	isLoading?: boolean;
}

export const CardUtilization = ({
	card = 'KEY_STATS',
	isLoading = false,
	DialogProps,
}: CardUtilizationProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);

	// vars
	const ChipStatusProps: CardSeverityProps['ChipStatusProps'] = {
		status: 'PENDING',
	};
	const currencyReporting = project?.currencies?.find((c) => c.type === 'REPORTING');
	const title = {
		children: t('utilization.title'),
	};

	let footnote = t('utilization.footnote.unavailable');
	let isActive = true;
	let isIndeterminate = false;
	let lineItems: CardSeverityProps['lineItems'] = [];
	let severity: CardSeverityProps['severity'] = null;
	let utilization;
	let value = 0;

	if (DialogProps) {
		if (DialogProps.utilization) {
			utilization = DialogProps.utilization;

			if (typeof utilization.value === 'number') {
				footnote = t('utilization.footnote.available');
				value = utilization.value;

				if (utilization.value <= 30) {
					ChipStatusProps.status = 'LOW';
					severity = 'error';
				} else if (utilization.value > 30 && utilization.value < 70) {
					ChipStatusProps.status = 'MINIMAL';
					severity = 'warning';
				} else if (utilization.value > 100) {
					ChipStatusProps.status = 'INDETERMINATE';
					severity = 'default';
					isIndeterminate = true;
				} else {
					ChipStatusProps.status = 'HIGH';
					severity = 'success';
				}
			}

			lineItems = [
				{
					label: t('expensed'),
					value: formatCurrency({
						currency: utilization?.currency,
						notation: 'compact',
						value: utilization?.out?.total || 0,
					}),
				},
				{
					label: t('disbursed'),
					value: formatCurrency({
						currency: utilization?.currency,
						notation: 'compact',
						value: utilization?.in?.total || 0,
					}),
				},
			];
		}
	}

	let valueFormatted = formatPercentage(value);

	if (isIndeterminate) {
		footnote = t('utilization.footnote.indeterminate');
		isActive = false;
		lineItems = [];
		valueFormatted = t('na');
	}

	if (card === 'KEY_STATS') {
		let empty = undefined;

		if (!featureFlags['UTILIZATION'] || !currencyReporting) {
			empty = {
				ChipProps: {
					label: t('unavailable'),
				},
				content: {
					children: t('utilization.noReportingCurrency'),
				},
			};

			if (!featureFlags['UTILIZATION']) {
				empty = {
					ChipProps: {
						label: t('comingSoon'),
					},
					content: {
						children: t('comingSoon.description'),
					},
				};
			}

			isActive = false;
		}

		return (
			<CardKeyStats
				dialog="UTILIZATION"
				DialogProps={DialogProps}
				empty={empty}
				isActive={isActive}
				isLoading={isLoading}
				stats={[
					{
						ChipStatusProps,
						footnote: {
							children: footnote,
						},
						lineItems,
						severity,
						title,
						value: valueFormatted,
					},
				]}
			/>
		);
	}

	return (
		<CardSeverity
			ChipStatusProps={ChipStatusProps}
			dialog="UTILIZATION"
			DialogProps={DialogProps}
			footnote={footnote}
			isActive={isActive}
			isLoading={isLoading}
			label={title}
			lineItems={lineItems}
			severity={severity}
			value={{
				children: valueFormatted,
			}}
		/>
	);
};
